import React, { useEffect, useRef, useState } from 'react';
import { useStore } from '../store';
import { ArtifactBlock } from './ArtifactBlock';
import { DocHierarchyViewer, hasDocHierarchy } from './DocHierarchyViewer';
import { I, ConfluenceMark } from './Shared';
import { downloadArtifactsZip } from '../lib/download';
import { buildProjectImageIndex, overlayStepArtifacts } from '../lib/docImages';
import {
  applyComments as apiApplyComments,
  listAttachments as apiListAttachments,
  attachmentDownloadUrl,
  type PersistedAttachment,
} from '../lib/api';
import { CommentList } from './CommentList';
import { AttachmentBar } from './AttachmentBar';
import type { Attachment } from '../types';

type PanelTab = 'artifact' | 'reviews';

export function ArtifactPanel() {
  const project = useStore((s) => s.project);
  const open = useStore((s) => s.rpOpen);
  const stepKey = useStore((s) => s.rpStepKey);
  const hide = useStore((s) => s.hidePanel);
  const openFs = useStore((s) => s.openFullscreen);
  const setPendingEdit = useStore((s) => s.setPendingEdit);
  const submitEdit = useStore((s) => s.submitEdit);
  const togglePin = useStore((s) => s.togglePin);
  const replaceArtifact = useStore((s) => s.replaceArtifact);
  const projectRole = useStore((s) => s.projectRole);
  const fetchComments = useStore((s) => s.fetchComments);
  const fetchPublishedDocs = useStore((s) => s.fetchPublishedDocs);
  const rpInitialTab = useStore((s) => s.rpInitialTab);
  const stepComments = useStore((s) => (stepKey ? s.stepComments[stepKey] : undefined));
  const publishedDocs = useStore((s) => (stepKey ? s.publishedDocs[stepKey] : undefined));

  const step = React.useMemo(() => {
    if (!project || !stepKey) return null;
    for (const ph of project.phases) {
      const s = ph.steps.find((x) => x.key === stepKey);
      if (s) return s;
    }
    return null;
  }, [project, stepKey]);

  // Project-wide image index so a page's relative <img> refs render inline, and
  // the step's artifacts with downstream-injected page content overlaid (e.g.
  // product-docs pages showing the screenshots docs-screenshots injected).
  const projectImageIndex = React.useMemo(
    () => (project ? buildProjectImageIndex(project) : undefined),
    [project],
  );
  const artifacts = React.useMemo(
    () => (project && step ? overlayStepArtifacts(project, step.key) : step?.artifacts ?? []),
    [project, step],
  );

  const [feedback, setFeedback] = useState('');
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  // Attachments already persisted for this project (idea-box + this step),
  // shown read-only with download links.
  const [persisted, setPersisted] = useState<PersistedAttachment[]>([]);
  const [artIdx, setArtIdx] = useState(0);
  const [activeTab, setActiveTab] = useState<PanelTab>('artifact');
  // Comment IDs currently being applied (LLM edit + reply + resolve in flight).
  const [applyingIds, setApplyingIds] = useState<Set<string>>(new Set());
  const importInputRef = useRef<HTMLInputElement>(null);
  const addInputRef = useRef<HTMLInputElement>(null);
  const [replacing, setReplacing] = useState(false);

  // Reset feedback, index, and tab when we switch steps.
  useEffect(() => {
    setFeedback(step?.pendingEdit ?? '');
    setAttachments([]);
    setArtIdx(0);
    setActiveTab(rpInitialTab ?? 'artifact');
  }, [step?.key, step?.pendingEdit, rpInitialTab]);

  // Fetch published docs and review comments when the step changes.
  useEffect(() => {
    if (step?.key && step.status === 'done') {
      void fetchPublishedDocs(step.key);
      void fetchComments(step.key);
    }
  }, [step?.key, step?.status, fetchPublishedDocs, fetchComments]);

  // Load persisted attachments (real projects only) so the user can see/download
  // the files that fed this step: project-level idea-box files + this step's.
  const projectId = project?.id;
  const curStepKey = step?.key;
  useEffect(() => {
    if (!projectId || !projectId.startsWith('p_') || !curStepKey) {
      setPersisted([]);
      return;
    }
    let cancelled = false;
    void apiListAttachments(projectId)
      .then((all) => {
        if (cancelled) return;
        setPersisted(all.filter((a) => a.stepKey === null || a.stepKey === curStepKey));
      })
      .catch(() => {
        if (!cancelled) setPersisted([]);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, curStepKey, step?.version, step?.status]);

  if (!open || !step) return null;

  const handleImprove = () => {
    if (!feedback.trim() && attachments.length === 0) return;
    if (project?.id.startsWith('p_')) {
      void submitEdit(step.key, feedback.trim(), attachments.length ? attachments : undefined);
      setAttachments([]);
    } else {
      setPendingEdit(step.key, feedback.trim());
    }
  };

  const isPinned = step.pinnedAtVersion != null;
  const cur = artifacts[artIdx] || null;
  const isDocViewer = hasDocHierarchy(artifacts);
  // Replacing an artifact's content is a write — only on real, editable projects.
  const canEdit = !!project?.id.startsWith('p_') && projectRole !== 'viewer';

  // Replace the currently-selected artifact's content, keeping its name (handles
  // artifacts whose names contain slashes, which a file name can't express).
  const handleReplaceFile = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file || !cur) return;
    setReplacing(true);
    try {
      await replaceArtifact(step.key, cur.name, await file.text());
    } catch (err: any) {
      window.alert(`Could not replace artifact: ${err?.message ?? 'unknown error'}`);
    } finally {
      setReplacing(false);
    }
  };

  // Import a file as an artifact named after the file. Adds it when absent,
  // replaces it when a same-named artifact exists. This is how a file is loaded
  // into a step that hasn't run yet (it has no artifacts to "replace").
  const handleAddFile = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setReplacing(true);
    try {
      await replaceArtifact(step.key, file.name, await file.text());
      setArtIdx(0);
      setActiveTab('artifact');
    } catch (err: any) {
      window.alert(`Could not import file: ${err?.message ?? 'unknown error'}`);
    } finally {
      setReplacing(false);
    }
  };
  const unresolvedCount = stepComments?.filter((c) => !c.resolved).length ?? 0;
  const hasComments = stepComments && stepComments.length > 0;

  return (
    <aside style={{ borderLeft: '1px solid var(--line)', minHeight: 0, display: 'flex', flexDirection: 'column', background: 'var(--surface)' }}>
      {/* header */}
      <div style={{ padding: 'var(--sp-xs) var(--sp-sm)', borderBottom: '1px solid var(--line)', display: 'flex', alignItems: 'center', gap: 10 }}>
        <span className="t-h3" style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{step.label}</span>
        {canEdit && (
          <>
            <button
              className="btn btn--ghost btn--sm btn--icon"
              disabled={replacing}
              onClick={() => addInputRef.current?.click()}
              title="Import a file as an artifact (works even before the step has run)"
            >
              {I.upload}
            </button>
            <input
              ref={addInputRef}
              type="file"
              style={{ display: 'none' }}
              onChange={(e) => void handleAddFile(e)}
            />
          </>
        )}
        <button className="btn btn--ghost btn--sm btn--icon" onClick={openFs} title="Fullscreen">{I.zoom}</button>
        <button
          className="btn btn--ghost btn--sm btn--icon"
          onClick={() => void togglePin(step.key)}
          title={isPinned ? 'Allow cascade re-runs' : 'Lock this output from cascades'}
        >
          {I.pin}
        </button>
        <button className="btn btn--ghost btn--sm btn--icon" onClick={hide} title="Close">{I.x}</button>
      </div>

      {isDocViewer ? (
        <DocHierarchyViewer artifacts={artifacts} imageIndex={projectImageIndex} />
      ) : (
        <>
          {/* toolbar: file selector + download + tab toggle */}
          {artifacts.length > 0 && (
            <div style={{ padding: '6px 10px', borderBottom: '1px solid var(--line)', background: 'var(--surface-2)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <select
                className="select"
                style={{ height: 24, width: 'auto', flex: 1, fontFamily: 'var(--font-mono)', fontSize: 11, minWidth: 0 }}
                value={artIdx}
                onChange={(e) => { setArtIdx(+e.target.value); setActiveTab('artifact'); }}
              >
                {artifacts.map((a, i) => <option key={a.name + i} value={i}>{a.name}</option>)}
              </select>
              <button
                className="btn btn--ghost btn--sm btn--icon"
                title={artifacts.length > 1 ? 'Download all as zip' : 'Download'}
                onClick={() => void downloadArtifactsZip(step.label, artifacts)}
              >
                {I.download}
              </button>
              {canEdit && cur && (
                <>
                  <button
                    className="btn btn--ghost btn--sm btn--icon"
                    disabled={replacing}
                    title={`Replace "${cur.name}" from a file`}
                    onClick={() => importInputRef.current?.click()}
                  >
                    {I.upload}
                  </button>
                  <input
                    ref={importInputRef}
                    type="file"
                    style={{ display: 'none' }}
                    onChange={(e) => void handleReplaceFile(e)}
                  />
                </>
              )}

              {/* published doc link */}
              {cur && publishedDocs && publishedDocs.length > 0 && (() => {
                const pubDoc = publishedDocs.find((d) => d.artifactName === cur.name);
                if (!pubDoc?.url) return null;
                return (
                  <a
                    href={pubDoc.url.startsWith('http') ? pubDoc.url : undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn--ghost btn--sm btn--icon"
                    data-demo-confluence
                    style={{ textDecoration: 'none', padding: 0, width: 22, height: 22 }}
                    title="View in Confluence"
                  >
                    <ConfluenceMark size={16} />
                  </a>
                );
              })()}

              {/* tab toggle: only show when there are comments */}
              {hasComments && (
                <>
                  <span style={{ width: 1, height: 18, background: 'var(--line)', margin: '0 2px' }} />
                  <button
                    className={`btn btn--sm ${activeTab === 'artifact' ? 'btn--accent' : 'btn--ghost'}`}
                    onClick={() => setActiveTab('artifact')}
                    style={{ fontSize: 11, padding: '2px 8px', height: 24 }}
                  >
                    Doc
                  </button>
                  <button
                    className={`btn btn--sm ${activeTab === 'reviews' ? 'btn--accent' : 'btn--ghost'}`}
                    onClick={() => setActiveTab('reviews')}
                    style={{ fontSize: 11, padding: '2px 8px', height: 24, position: 'relative' }}
                  >
                    Reviews
                    {unresolvedCount > 0 && (
                      <span
                        style={{
                          position: 'absolute',
                          top: -4,
                          right: -6,
                          background: 'var(--err)',
                          color: '#fff',
                          borderRadius: 8,
                          padding: '0 5px',
                          fontSize: 9,
                          fontWeight: 700,
                          lineHeight: '16px',
                          minWidth: 16,
                          textAlign: 'center',
                        }}
                      >
                        {unresolvedCount}
                      </span>
                    )}
                  </button>
                </>
              )}
            </div>
          )}

          {/* content area: artifact OR reviews */}
          <div className="scroll" style={{ flex: 1, padding: 'var(--sp-sm)', background: 'var(--surface)' }}>
            {activeTab === 'artifact' ? (
              artifacts.length === 0 ? (
                <div className="muted" style={{ padding: 32, textAlign: 'center' }}>
                  <div>No artifacts loaded</div>
                  {canEdit && (
                    <button
                      className="btn btn--sm"
                      disabled={replacing}
                      style={{ marginTop: 12 }}
                      onClick={() => addInputRef.current?.click()}
                      title="Import a file as an artifact without running this step"
                    >
                      {I.upload} Import a file
                    </button>
                  )}
                </div>
              ) : cur ? (
                <ArtifactBlock artifact={cur} imageIndex={projectImageIndex} />
              ) : null
            ) : (
              stepComments && (
                <CommentList
                  comments={stepComments}
                  applyingIds={applyingIds}
                  onApplySelected={async (selected) => {
                    const ids = selected.map((c) => c.id);
                    setApplyingIds(new Set(ids));
                    const apiId = project?.id.startsWith('p_') ? project.id : 'demo';
                    try {
                      await apiApplyComments(apiId, step!.key, ids);
                    } catch (e) {
                      console.error('Failed to apply comments:', e);
                    } finally {
                      await fetchComments(step!.key);
                      void fetchPublishedDocs(step!.key);
                      setApplyingIds(new Set());
                    }
                  }}
                />
              )
            )}
          </div>
        </>
      )}

      {/* feedback */}
      <div style={{ padding: 'var(--sp-sm)', borderTop: '1px solid var(--line)', background: 'var(--bg-sunken)' }}>
        <div className="label" style={{ marginBottom: 8 }}>Improve · re-runs with your feedback</div>
        <div style={{ position: 'relative' }}>
          <textarea
            className="textarea"
            rows={3}
            placeholder={`Describe how to improve "${isDocViewer ? step.label : (cur?.name ?? step.label)}"...`}
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            style={{ background: 'var(--surface)', paddingRight: 90 }}
          />
          <button
            className="btn btn--accent btn--sm"
            style={{ position: 'absolute', right: 8, bottom: 8 }}
            disabled={!feedback.trim() && attachments.length === 0}
            onClick={handleImprove}
          >
            Improve {I.spark}
          </button>
        </div>
        {persisted.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <div className="t-micro" style={{ color: 'var(--ink-3)', marginBottom: 4 }}>
              Attached to this step
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {persisted.map((a) => (
                <a
                  key={a.id}
                  className="chip"
                  href={projectId ? attachmentDownloadUrl(projectId, a.id) : undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={`${a.name} — download`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    maxWidth: 220,
                    height: 26,
                    fontSize: 12,
                    textDecoration: 'none',
                    color: 'var(--ink)',
                  }}
                >
                  <span style={{ display: 'inline-flex', color: 'var(--ink-3)' }}>{I.fileText}</span>
                  <span
                    style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {a.name}
                  </span>
                </a>
              ))}
            </div>
          </div>
        )}
        <div style={{ marginTop: 8 }}>
          {/* Attachments are only persisted for real, editable projects. Disable
              the picker for viewers and demo/local projects so files can't be
              silently dropped on submit. */}
          <AttachmentBar attachments={attachments} onChange={setAttachments} compact disabled={!canEdit} />
        </div>
      </div>
    </aside>
  );
}
